<script lang="ts">
	// Public landing page (signed-out visitors at /). Markup and styles only; sign-in
	// state is decided by Home.svelte. Colours and type are the Flare / Aqua palette
	// (warm ground, near-black ink, one orange accent, IBM Plex); dark follows the
	// app's `html.dark` class.
	const year = new Date().getFullYear();
	const products = [
		{
			name: 'Flare',
			tag: 'Code. Run. Learn. Compete.',
			text: 'An online coding platform: write, run and share code in dozens of languages from the browser, with projects, GitHub import and live collaboration.',
			href: 'https://flare.koru.africa',
			link: 'flare.koru.africa'
		},
		{
			name: 'Koru platform',
			tag: 'Where code grows and deploys',
			text: 'Git hosting with repositories, pull requests, deployments and databases in one place. Push code and ship it.',
			href: '#/register',
			link: 'Create an account'
		},
		{
			name: 'Speed',
			tag: 'Kenya speed limits',
			text: 'A database and API of road speed limits across Kenya, searchable on a map and open to apps that need it.',
			href: 'https://speed.koru.africa',
			link: 'speed.koru.africa'
		},
		{
			name: 'Specra',
			tag: 'Documentation that ships',
			text: 'Documentation sites for software projects: write in Markdown, publish, keep it next to the code.',
			href: 'https://specra-docs.com',
			link: 'specra-docs.com'
		}
	];
	const services = [
		{
			n: '01',
			title: 'Web platforms',
			text: 'Products and internal tools built end to end: accounts, billing, dashboards, the parts people use every day.'
		},
		{
			n: '02',
			title: 'APIs and integrations',
			text: 'Services that connect systems: payments (M-Pesa, Pesapal, Stripe), maps, sign-in with Google and GitHub, data feeds.'
		},
		{
			n: '03',
			title: 'Run and operate',
			text: 'We deploy what we build and keep it running: servers, databases, backups, monitoring and the releases after launch.'
		}
	];
</script>

<svelte:head>
	<title>Koru · We build software solutions</title>
	<meta name="description" content="Koru builds software solutions: web platforms, APIs and developer tools, designed, built and run by us." />
	<link rel="preconnect" href="https://fonts.googleapis.com" />
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
	<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap" />
</svelte:head>

<div class="koru-landing">
	<header class="top">
		<a class="mark" href="#/" aria-label="Koru home">
			<img src="/koru.svg" alt="" width="28" height="28" />
			<span>Koru</span>
		</a>
		<nav aria-label="Primary">
			<a href="#products">Products</a>
			<a href="#services">What we do</a>
			<a href="https://blog.koru.africa" rel="noopener">Writing</a>
		</nav>
		<div class="acts">
			<a class="btn" href="#/login">Sign in</a>
			<a class="btn pri" href="#/register">Get started</a>
		</div>
	</header>

	<main class="container">
		<section class="hero">
			<div>
				<h1>Koru builds <em>software solutions.</em></h1>
				<p class="lede">
					Web platforms, APIs and developer tools, designed, built and run by one team. We make products of our
					own and we build for others; the same people do both.
				</p>
				<div class="cta">
					<a class="btn pri lg" href="#products">See our products</a>
					<a class="btn lg" href="https://flare.koru.africa" rel="noopener">Try Flare</a>
				</div>
			</div>
			<div class="facts" aria-label="At a glance">
				<div class="fact"><div class="grp">Products</div><b class="num">{products.length}</b><span>live and in use today</span></div>
				<div class="fact"><div class="grp">Stack</div><b>Go, Rust, Svelte</b><span>PostgreSQL, Caddy, plain Linux</span></div>
				<div class="fact"><div class="grp">Base</div><b>Kenya</b><span>built for local payments and data</span></div>
			</div>
		</section>

		<section class="section" id="products">
			<div class="section-head"><h2>Products</h2><span class="sub">Built, shipped and run by Koru.</span></div>
			<div class="cards">
				{#each products as p}
					<a class="card" href={p.href} rel="noopener">
						<div class="grp">{p.tag}</div>
						<h3>{p.name}</h3>
						<p>{p.text}</p>
						<span class="more">{p.link} →</span>
					</a>
				{/each}
			</div>
		</section>

		<section class="section" id="services">
			<div class="section-head"><h2>What we do</h2><span class="sub">From the first sketch to the server it runs on.</span></div>
			<div class="steps">
				{#each services as s}
					<div class="step"><span class="n">{s.n}</span><h3>{s.title}</h3><p>{s.text}</p></div>
				{/each}
			</div>
		</section>

		<section class="section band">
			<div>
				<h2>Have something to build?</h2>
				<p class="sub">Tell us what it should do and who it is for. We answer with a plan, not a pitch deck.</p>
			</div>
			<div class="cta">
				<a class="btn pri lg" href="#/register">Start on the platform</a>
				<a class="btn lg" href="https://blog.koru.africa" rel="noopener">Read our notes</a>
			</div>
		</section>

		<footer class="foot">
			<span>© {year} Koru. All rights reserved.</span>
			<span class="sp"></span>
			<a href="#/terms-and-conditions">Terms</a>
			<a href="#/privacy-policy">Privacy</a>
			<a href="https://blog.koru.africa" rel="noopener">Writing</a>
			<a href="https://github.com/Arthur-Kamau" rel="noopener">GitHub</a>
		</footer>
	</main>
</div>

<style>
	/* Flare design tokens (apps/web/static/css/tokens.css), light. */
	.koru-landing {
		--brand: #e55b1f;
		--brand-deep: #c2470f;
		--brand-soft: #fff3ec;
		--brand-line: #f6cdb6;
		--on-brand: #ffffff;
		--ground: #f6f5f3;
		--surface: #ffffff;
		--surface-2: #faf9f7;
		--ink: #141414;
		--ink-2: #4a4744;
		--ink-3: #8a857e;
		--line: #e7e3de;
		--line-strong: #d2ccc4;
		--rail: #141414;
		--rail-ink: #a8a29a;
		--shadow: 0 1px 3px rgba(20, 20, 20, 0.06), 0 8px 24px -16px rgba(20, 20, 20, 0.22);
		--ui: 'IBM Plex Sans', ui-sans-serif, system-ui, 'Segoe UI', sans-serif;
		--mono: 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace;
		--r: 6px;
		--r-lg: 10px;
		--content-max: 1180px;
		color-scheme: light;

		min-height: 100vh;
		background: var(--ground);
		color: var(--ink);
		font-family: var(--ui);
		font-size: 14px;
		line-height: 1.5;
		-webkit-font-smoothing: antialiased;
	}
	/* Dark: warm blacks, the same accent lifted for contrast. The app keeps its theme on html.dark. */
	:global(html.dark) .koru-landing {
		--brand: #ff7a3d;
		--brand-deep: #e55b1f;
		--brand-soft: #2e1a10;
		--brand-line: #54301c;
		--on-brand: #1a0e07;
		--ground: #121110;
		--surface: #1a1917;
		--surface-2: #211f1d;
		--ink: #f4f1ee;
		--ink-2: #b5afa8;
		--ink-3: #847e76;
		--line: #2e2b27;
		--line-strong: #403c37;
		--rail: #0c0c0b;
		--shadow: 0 1px 3px rgba(0, 0, 0, 0.5), 0 10px 28px -18px rgba(0, 0, 0, 0.8);
		color-scheme: dark;
	}
	/* global.css pads the body for the app; the landing page bleeds to the edges. */
	:global(body:has(.koru-landing)) { padding: 0; }

	.koru-landing * { box-sizing: border-box; }
	.koru-landing a { color: inherit; text-decoration: none; }
	.koru-landing h1, .koru-landing h2, .koru-landing h3, .koru-landing p { margin: 0; }

	/* Top bar (public pages: white bar) */
	.top {
		position: sticky; top: 0; z-index: 10;
		display: flex; align-items: center; gap: 28px;
		height: 52px; padding: 0 24px;
		background: var(--surface); border-bottom: 1px solid var(--line);
	}
	.mark { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; font-size: 16px; letter-spacing: -0.01em; }
	.mark img { display: block; border-radius: 6px; }
	.top nav { display: flex; gap: 20px; font-size: 13.5px; color: var(--ink-2); }
	.top nav a:hover { color: var(--ink); }
	.acts { margin-left: auto; display: flex; gap: 8px; }

	.btn {
		display: inline-flex; align-items: center; justify-content: center; gap: 6px;
		height: 32px; padding: 0 12px;
		border: 1px solid var(--line-strong); border-radius: var(--r);
		background: var(--surface); color: var(--ink);
		font: 500 13.5px var(--ui); white-space: nowrap; cursor: pointer;
		transition: background 0.12s, border-color 0.12s;
	}
	.btn:hover { background: var(--surface-2); border-color: var(--ink-3); }
	.btn.pri { background: var(--brand); border-color: var(--brand); color: var(--on-brand); }
	.btn.pri:hover { background: var(--brand-deep); border-color: var(--brand-deep); }
	.btn.lg { height: 40px; padding: 0 18px; font-size: 14.5px; }

	.container { max-width: var(--content-max); margin: 0 auto; padding: 0 24px; }

	/* Hero */
	.hero { display: grid; grid-template-columns: 1.3fr 1fr; gap: 48px; align-items: center; padding: 72px 0 48px; }
	.hero h1 { font-size: 44px; line-height: 1.08; letter-spacing: -0.02em; font-weight: 700; }
	.hero h1 em { font-style: normal; color: var(--brand); }
	.lede { margin-top: 16px; max-width: 54ch; font-size: 15.5px; color: var(--ink-2); }
	.cta { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 22px; }

	.facts { display: grid; gap: 12px; }
	.fact {
		padding: 14px 16px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg);
		display: grid; gap: 2px; box-shadow: var(--shadow);
	}
	.grp { font: 600 11px var(--ui); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-3); }
	.fact b { font-size: 15.5px; }
	.fact .num { font: 500 28px var(--mono); line-height: 1.1; }
	.fact span { font-size: 12.5px; color: var(--ink-3); }

	/* Sections */
	.section { padding: 48px 0; border-top: 1px solid var(--line); }
	.section-head { display: flex; align-items: baseline; gap: 14px; margin-bottom: 22px; }
	.section-head h2, .band h2 { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; }
	.sub { font-size: 13.5px; color: var(--ink-3); }

	.cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
	.card {
		display: grid; gap: 8px; align-content: start;
		padding: 20px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg);
		transition: border-color 0.12s, box-shadow 0.12s;
	}
	.card:hover { border-color: var(--brand-line); box-shadow: var(--shadow); }
	.card h3 { font-size: 17px; font-weight: 600; }
	.card p { color: var(--ink-2); }
	.card .more { font: 500 12.5px var(--mono); color: var(--brand); }

	.steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
	.step { padding: 20px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg); display: grid; gap: 8px; align-content: start; }
	.step .n { font: 500 12.5px var(--mono); color: var(--brand); }
	.step h3 { font-size: 15.5px; font-weight: 600; }
	.step p { color: var(--ink-2); }

	.band {
		display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 22px;
		margin: 0 0 48px; padding: 32px; border: 1px solid var(--brand-line); border-radius: var(--r-lg);
		background: var(--brand-soft);
	}
	.band .sub { display: block; margin-top: 6px; color: var(--ink-2); }
	.band .cta { margin: 0; }

	.foot { display: flex; flex-wrap: wrap; gap: 16px; padding: 22px 0 32px; border-top: 1px solid var(--line); font-size: 12.5px; color: var(--ink-3); }
	.foot a:hover { color: var(--ink); }
	.sp { flex: 1; }

	@media (max-width: 900px) {
		.hero { grid-template-columns: 1fr; padding-top: 48px; }
		.hero h1 { font-size: 34px; }
		.cards, .steps { grid-template-columns: 1fr; }
		.top nav { display: none; }
	}
	@media (max-width: 640px) {
		.top, .container { padding-left: 16px; padding-right: 16px; }
		.section-head { flex-direction: column; gap: 4px; }
		.band { padding: 22px; }
	}
</style>
